import { useState } from "react";
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, ClipboardCheck, ArrowLeft, Info, ImageOff, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { useAuth } from "../context/AuthContext";

export default function CatalogItem() {
  const { state } = useLocation();
  const { itemId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const item = state?.item;

  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(item ? parseInt(item.moq) || 1 : 1);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  if (!item) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <ImageOff className="mx-auto text-stone-300" size={40} />
        <p className="text-stone-500 mt-3">Detail barang tidak tersedia. Silakan buka dari katalog.</p>
        <Link to="/katalog" className="inline-block mt-4 text-emerald-700 font-semibold">← Kembali ke Katalog</Link>
      </div>
    );
  }

  const images = (item.images && item.images.length ? item.images : [item.image]).filter(Boolean);
  const tiers = item.tiers && item.tiers.length ? item.tiers : null;

  const ajukan = async () => {
    if (!user) { toast.error("Silakan masuk dulu untuk mengajukan barang"); nav("/login"); return; }
    setSaving(true);
    try {
      await api.post("/requests", {
        title: item.title, source_url: item.url, image_url: item.image,
        qty: parseInt(qty) || 1, notes: message,
      });
      toast.success("Barang diajukan! Admin akan cek harga aslinya & hubungi penjual, lalu kirim total + ongkir ke akunmu.");
      nav("/permintaan");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <button onClick={() => nav(-1)} data-testid="item-back" className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 mb-5"><ArrowLeft size={16} />Kembali</button>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-stone-100 rounded-2xl overflow-hidden border border-stone-200">
            {images[active] ? <img src={images[active]} alt={item.title} referrerPolicy="no-referrer" className="w-full h-full object-cover" data-testid="item-main-image" /> : null}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
              {images.map((im, i) => (
                <button key={i} data-testid={`item-thumb-${i}`} onClick={() => setActive(i)}
                  className={`w-16 h-16 shrink-0 rounded-lg overflow-hidden border-2 ${active === i ? "border-emerald-500" : "border-stone-200"}`}>
                  <img src={im} alt="" referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <h1 className="font-display text-2xl font-bold text-stone-900 leading-snug" data-testid="item-title">{item.title}</h1>
          <p className="text-sm text-stone-400 mt-1">Minimal order {item.moq} pcs</p>

          <div className="mt-4">
            <p className="text-3xl font-display font-extrabold text-emerald-700" data-testid="item-price">{item.price_idr != null ? `± ${rupiah(item.price_idr)}` : `± Rp ${item.price_masked}`}<span className="text-sm font-normal text-stone-400"> / pcs</span></p>
            <p className="text-xs text-stone-400">harga perkiraan, belum tentu harga asli</p>
            {item.price_idr == null && (
              <Link to="/member" data-testid="item-member-cta" className="mt-2 inline-flex items-center gap-2 bg-amber-500 text-white text-sm font-semibold px-3 py-1.5 rounded-lg"><Crown size={15} />Jadi member untuk lihat harga lengkap</Link>
            )}
          </div>

          {/* Tiered wholesale prices */}
          {tiers && (
            <div className="mt-5 border border-stone-200 rounded-xl overflow-hidden" data-testid="item-tiers">
              <div className="bg-stone-50 px-4 py-2 text-xs font-semibold text-stone-500 uppercase tracking-wide">Harga Grosir Bertingkat</div>
              <table className="w-full text-sm">
                <tbody>
                  {tiers.map((t, i) => (
                    <tr key={i} className="border-t border-stone-100">
                      <td className="px-4 py-2 text-stone-600">{t.min}{t.max && t.max !== -1 ? `–${t.max}` : "+"} pcs</td>
                      <td className="px-4 py-2 text-right font-semibold text-emerald-700">{t.price_idr != null ? `± ${rupiah(t.price_idr)}` : `± Rp ${t.price_masked}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-5 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-800">
            <Info size={15} className="mt-0.5 shrink-0" />
            <span className="leading-relaxed">Ini harga perkiraan. Ajukan jumlah & pesanmu, admin akan hubungi penjual untuk memastikan harga asli lalu kirim total + ongkir final ke akunmu.</span>
          </div>

          {/* Ajukan form */}
          <div className="mt-5 bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
            <div>
              <label className="text-sm font-medium text-stone-600">Jumlah (pcs)</label>
              <input data-testid="item-qty" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
                className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-600">Pesan untuk Admin</label>
              <textarea data-testid="item-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                placeholder="Contoh: minta warna hitam, cek harga paling murah, tanya ready stok..."
                className="mt-1 w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-emerald-400 resize-none" />
            </div>
            <button onClick={ajukan} disabled={saving} data-testid="item-ajukan"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={17} /> : <ClipboardCheck size={17} />}Ajukan untuk Dicek Admin
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
